import {
  Checkbox,
  FormControlLabel,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import type { ResourceType } from '@repo/api-gw-sdk';
import { useEffect, useRef, useState } from 'react';

import ButtonWithIcon from '@/components/buttons/buttonWithIcon';
import { ResourceTypeIndicator } from '@/components/cloudProviderIndicator';
import { ResourceTypes } from '@/data/inventory/resourceTypes';

export const RestrictedResourceTypesSelector = ({
  resourceTypes,
  onChange,
  restrictedResourceTypes,
}: {
  resourceTypes: ResourceType[];
  onChange: (resourceTypes: ResourceType[]) => void;
  restrictedResourceTypes?: ResourceType[];
}) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  return (
    <>
      <ButtonWithIcon
        ref={anchorRef}
        onClick={() => setPopoverOpen(true)}
        data-testid='create-backup-policy-resource-type-select'
        disableFocusRipple
        text={resourceTypes.map((x) => ResourceTypes[x].displayName).join(', ')}
        icon='material-symbols-arrow-drop-down-rounded'
        iconPosition='right'
      />
      <RestrictedResourceTypesSelectorPopover
        anchorRef={anchorRef}
        resourceTypes={resourceTypes}
        restrictedResourceTypes={restrictedResourceTypes}
        onChange={(value) => {
          setPopoverOpen(false);
          onChange(value);
        }}
        popoverOpen={popoverOpen}
      />
    </>
  );
};

const RestrictedResourceTypesSelectorPopover = ({
  resourceTypes,
  onChange,
  anchorRef,
  popoverOpen,
  restrictedResourceTypes,
}: {
  popoverOpen: boolean;
  resourceTypes: ResourceType[];
  onChange: (resourceTypes: ResourceType[]) => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  restrictedResourceTypes?: ResourceType[];
}) => {
  const [value, setValue] = useState<ResourceType[]>(resourceTypes);
  useEffect(() => {
    setValue(resourceTypes);
  }, [resourceTypes]);

  return (
    <Popover
      disablePortal
      open={popoverOpen}
      anchorEl={anchorRef.current}
      onClose={() => onChange(value.sort())}
    >
      <Stack className='p-[24px] w-[308px]' gap='24px'>
        <Typography variant='body1'>
          Select at least one resource type.
        </Typography>
        {restrictedResourceTypes?.map((resourceType) => (
          <FormControlLabel
            key={resourceType}
            control={
              <Checkbox
                checked={value.includes(resourceType)}
                onChange={(event, value) =>
                  setValue((state) =>
                    value
                      ? [...state, resourceType]
                      : state.filter((x) => x !== resourceType)
                  )
                }
              />
            }
            label={
              <Stack marginLeft='12px'>
                <ResourceTypeIndicator resourceType={resourceType} />
              </Stack>
            }
          />
        ))}
      </Stack>
    </Popover>
  );
};
